/**
 * Single presentational SRP component: same markup and DOM structure in static and interactive modes.
 * Uses the same complex components as the full +Page (SearchHeader, SearchSidebar, SearchResults,
 * SearchPagination). Used by SSR (#srp-ssr) and by the client island (SrpApp).
 * Static mode uses no IntlProvider; all strings come from model.staticStrings.
 * When chromeFromParent, uses SrpViewInteractiveIslandContent so hits/sort/filters are fully interactive.
 */
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { StaticSRPHeader } from '../../pages/index/static/StaticSRPHeader';
import { StaticSRPHeaderWithStrings } from '../../pages/index/static/StaticSRPHeader';
import { StaticMobileBottomNav } from '../../pages/index/static/StaticMobileBottomNav';
import { SearchHeader } from '../../pages/index/components/SearchHeader';
import { MarketHeaderStatic } from '../../components/MarketHeaderStatic';
import { CompanyPageHeaderStatic } from '../../components/CompanyPageHeaderStatic';
import { SearchSidebar } from '../../pages/index/components/SearchSidebar';
import { SearchResults } from '../../pages/index/components/SearchResults';
import { SearchPagination } from '../../pages/index/components/SearchPagination';
import { useViewMode } from '../../pages/index/hooks/useViewMode';
import { useSearchSort, useSearchQuery, useSearchFilters, useSearchPage } from '../../contexts/SearchContext';
import { usePushIndexUiState } from '../../hooks/usePushIndexUiState';
import Footer from '../../components/Footer';
import { FeedbackModal } from '../../components/FeedbackModal';
import { getPageUrlFromSrpViewModel } from './buildSrpViewModel';
import type { SrpViewModel } from './SrpViewModel';

export interface SrpViewProps {
  model: SrpViewModel;
  mode: 'static' | 'interactive';
  /** When true (e.g. island inside SearchWrapper), skip the standalone header; parent provides chrome (logo, filters, nav). */
  chromeFromParent?: boolean;
}

const noop = () => {};

const indexBaseMap: Record<string, string> = { en: 'en_search', fi: 'fi_hae', sv: 'sv_sok' };

/**
 * Island-only: uses SearchContext hooks so sort, view mode, pagination and hits are interactive (CustomHits, save/state buttons).
 */
function SrpViewInteractiveIslandContent({ model }: { model: SrpViewModel }): React.ReactElement {
  const intl = useIntl();
  const { viewMode, setViewMode } = useViewMode({ initialLayout: model.viewMode });
  const { sort, setSort } = useSearchSort();
  const { query, setQuery } = useSearchQuery();
  const { filters, setFilters } = useSearchFilters();
  const { setPage } = useSearchPage();
  const { pushIndexUiState } = usePushIndexUiState();
  const [helpTrayOpen, setHelpTrayOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);

  const indexPrefix = indexBaseMap[model.locale] || 'en_search';
  const effectiveSort = sort || model.query.sort || 'relevance';
  const currentRefinement =
    effectiveSort === 'date'
      ? `${indexPrefix}_postedDate_desc`
      : effectiveSort === 'salary'
        ? `${indexPrefix}_salaryRangeMin_desc`
        : indexPrefix;

  const handleSortChange = (value: string) => {
    let sortPreference: 'relevance' | 'date' | 'salary' = 'relevance';
    if (value.includes('postedDate_desc')) sortPreference = 'date';
    else if (value.includes('salaryRangeMin_desc')) sortPreference = 'salary';
    setSort(sortPreference);
    try {
      const u = typeof window !== 'undefined' ? new URL(window.location.href) : null;
      if (u) {
        const oldKeys = Array.from(u.searchParams.keys()).filter((k) => k.includes('[sort]'));
        oldKeys.forEach((k) => u.searchParams.delete(k));
        if (sortPreference === 'relevance') u.searchParams.delete('sort');
        else u.searchParams.set('sort', sortPreference);
        window.location.assign(u.toString());
      }
    } catch {
      // no-op
    }
  };

  const onClearSearch = () => {
    setQuery('');
    setFilters({});
    setPage(0);
    setSort('relevance');
    pushIndexUiState({ query: '', refinementList: {}, page: 0, sort: 'relevance' });
  };

  const hasActiveSearch = Boolean(query) || (filters && Object.keys(filters).length > 0);

  // Use only live filters for header so clearing the filter removes the header (no fallback to model)
  const filtersObj = filters && typeof filters === 'object' ? (filters as Record<string, string[]>) : {};
  const headerCompanyId =
    Array.isArray(filtersObj.companyId) && filtersObj.companyId.length > 0 ? filtersObj.companyId[0] : null;
  const headerLocation =
    Array.isArray(filtersObj.location) && filtersObj.location.length === 1 ? filtersObj.location[0] : null;

  return (
    <div className="w-full" data-srp-mode="interactive">
      <div className={`max-w-screen-2xl mx-auto sm:px-0 md:px-2 lg:px-8 ${headerLocation ? '' : 'pt-1 sm:pt-1 md:pt-2'}`}>
        <SearchHeader
          location={headerLocation}
          companyId={headerCompanyId}
          locale={model.locale as 'en' | 'fi' | 'sv'}
          nbHits={model.pagination.nbHits}
          locationTrendsData={model.locationTrendsData}
        />
        <div className="ais-InstantSearch @container w-full min-w-0 pt-1 sm:pt-1 md:pt-2 flex flex-col gap-6">
          <SearchSidebar
            intl={intl}
            viewMode={viewMode}
            setViewMode={setViewMode}
            currentRefinement={currentRefinement}
            handleSortChange={handleSortChange}
            hasActiveSearch={hasActiveSearch}
            onClearSearch={onClearSearch}
            location={headerLocation}
            companyId={headerCompanyId}
            feedbackModalOpen={feedbackModalOpen}
            setFeedbackModalOpen={setFeedbackModalOpen}
          />
          <SearchResults viewMode={viewMode} hasNoResults={model.hasNoResults} query={model.query.query} />
        </div>
        {viewMode !== 'map' && (
          <SearchPagination
            viewMode={viewMode}
            helpTrayOpen={helpTrayOpen}
            setHelpTrayOpen={setHelpTrayOpen}
          />
        )}
      </div>
      <FeedbackModal
        isOpen={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
        source="search_results"
        metadata={{ page: model.pathname }}
      />
      <Footer locale={model.locale} strings={model.footerStrings} />
    </div>
  );
}

/**
 * Renders the full SRP layout. In static mode uses pre-generated strings (no useIntl).
 * In interactive mode uses IntlProvider from layout.
 */
export function SrpView({ model, mode, chromeFromParent }: SrpViewProps): React.ReactElement {
  const isStatic = mode === 'static';
  if (isStatic) {
    // Same wrapper and content structure as SearchWrapper + SrpViewInteractiveIslandContent so CSS and layout match after swap.
    return (
      <div
        className="relative flex flex-col h-screen mobile-viewport-height overflow-hidden lg:overflow-visible"
        data-srp-mode="static"
      >
        <StaticSRPHeaderWithStrings
          locale={model.locale}
          query={model.query.query}
          staticStrings={model.staticStrings}
          userAvatarUrl={model.userAvatarUrl}
          userName={model.userName}
          facets={model.facets}
          refinementList={model.query.refinementList}
          pathname={model.pathname}
          indexName={model.indexName}
        />
        <div className="flex-1 flex lg:flex-row lg:relative lg:overflow-hidden min-h-0">
          <div className="flex-1 bg-gray-50 dark:bg-gray-900 overflow-x-hidden overflow-y-auto min-h-0 pt-1 sm:pt-1 md:pt-2 pb-20 lg:pb-0 sm:px-0 md:px-2 lg:px-8">
            <div className={`max-w-screen-2xl mx-auto sm:px-0 md:px-2 lg:px-8 ${model.location || model.companyId ? '' : 'pt-1 sm:pt-1 md:pt-2'}`}>
              {model.location && model.marketHeaderStatic && model.staticStrings.marketHeader && (
                <section className="-mx-2 sm:-mx-2 md:-mx-2 lg:-mx-8 -mt-1 sm:-mt-1 md:-mt-2" aria-labelledby="location-header-title">
                  <MarketHeaderStatic
                    location={model.location}
                    nbHits={model.pagination.nbHits}
                    locationTrendsData={model.locationTrendsData}
                    marketHeaderStatic={model.marketHeaderStatic}
                    strings={model.staticStrings.marketHeader}
                  />
                </section>
              )}
              {model.companyId && model.companyHeaderStatic && model.staticStrings.companyHeader && (
                <section className="sm:px-0 md:px-2 pt-1 sm:pt-1 md:pt-2" aria-labelledby="company-header-title">
                  <CompanyPageHeaderStatic
                    data={model.companyHeaderStatic}
                    strings={model.staticStrings.companyHeader}
                  />
                </section>
              )}
              <div className="ais-InstantSearch @container w-full min-w-0 pt-1 sm:pt-1 md:pt-2 flex flex-col gap-6">
                <SearchSidebar
                  viewMode={model.viewMode}
                  setViewMode={noop}
                  currentRefinement={model.currentRefinement}
                  handleSortChange={noop}
                  hasActiveSearch={model.hasActiveSearch}
                  onClearSearch={noop}
                  location={model.location}
                  companyId={model.companyId}
                  staticStatsText={model.statsText}
                  staticShowFinlandSubtitle={model.showFinlandSubtitle}
                  staticStrings={model.staticStrings}
                  initialLastSeenOption={model.initialLastSeenOption}
                />
                <SearchResults
                  viewMode={model.viewMode}
                  hasNoResults={model.hasNoResults}
                  query={model.query.query}
                  staticJobs={model.jobs}
                  staticViewMode={model.viewMode}
                  staticStrings={model.staticStrings}
                  isToolsPage={model.isToolsPage}
                />
              </div>
              {model.viewMode !== 'map' && (
                <SearchPagination
                  viewMode={model.viewMode}
                  helpTrayOpen={false}
                  setHelpTrayOpen={noop}
                  staticPagination={model.pagination}
                  getPageUrl={(page: number) => getPageUrlFromSrpViewModel(model, page)}
                  staticStrings={model.staticStrings}
                  loginReturnToPath={getPageUrlFromSrpViewModel(model, model.pagination.currentPage)}
                />
              )}
            </div>
            <Footer locale={model.locale} strings={model.footerStrings} />
          </div>
        </div>
        <StaticMobileBottomNav staticStrings={model.staticStrings} />
      </div>
    );
  }

  const intl = useIntl();

  // When chrome is from parent (e.g. island inside SearchWrapper), use fully interactive content:
  // CustomHits (save/state buttons work), real sort/filter handlers, CustomPagination.
  if (chromeFromParent) {
    return (
      <SrpViewInteractiveIslandContent model={model} />
    );
  }

  return (
    <div
      className="relative flex flex-col h-screen mobile-viewport-height overflow-hidden lg:overflow-visible"
      data-srp-mode="interactive"
    >
      <StaticSRPHeader locale={model.locale} query={model.query.query} />
      <div className="flex-1 flex lg:flex-row lg:relative lg:overflow-hidden min-h-0">
        <div className="flex-1 bg-gray-50 dark:bg-gray-900 overflow-x-hidden overflow-y-auto min-h-0 pt-1 sm:pt-1 md:pt-2 pb-20 lg:pb-0 sm:px-0 md:px-2 lg:px-8">
          <div className="w-full bg-gray-50 dark:bg-gray-900 pt-1 sm:pt-1 md:pt-2">
            <div className={`max-w-screen-2xl mx-auto sm:px-0 md:px-2 lg:px-8 ${model.location ? '' : 'pt-1 sm:pt-1 md:pt-2'}`}>
              <SearchHeader
                location={model.location}
                companyId={model.companyId}
                locale={model.locale as 'en' | 'fi' | 'sv'}
                nbHits={model.pagination.nbHits}
                locationTrendsData={model.locationTrendsData}
              />
              <div className="ais-InstantSearch @container w-full min-w-0 pt-1 sm:pt-1 md:pt-2 flex flex-col gap-6">
                <SearchSidebar
                  intl={intl}
                  viewMode={model.viewMode}
                  setViewMode={noop}
                  currentRefinement={model.currentRefinement}
                  handleSortChange={noop}
                  hasActiveSearch={model.hasActiveSearch}
                  onClearSearch={noop}
                  location={model.location}
                  companyId={model.companyId}
                  staticStatsText={model.statsText}
                  staticShowFinlandSubtitle={model.showFinlandSubtitle}
                />
                <SearchResults
                  viewMode={model.viewMode}
                  hasNoResults={model.hasNoResults}
                  query={model.query.query}
                  staticJobs={model.jobs}
                  staticViewMode={model.viewMode}
                />
              </div>
              {model.viewMode !== 'map' && (
                <SearchPagination
                  viewMode={model.viewMode}
                  helpTrayOpen={false}
                  setHelpTrayOpen={noop}
                  staticPagination={model.pagination}
                  getPageUrl={(page: number) => getPageUrlFromSrpViewModel(model, page)}
                  staticStrings={model.staticStrings}
                  loginReturnToPath={getPageUrlFromSrpViewModel(model, model.pagination.currentPage)}
                />
              )}
            </div>
          </div>
          <Footer locale={model.locale} strings={model.footerStrings} />
        </div>
      </div>
      <div className="lg:hidden h-14 flex-shrink-0 bg-gray-100 dark:bg-gray-800" aria-hidden />
    </div>
  );
}
